import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Link as RouterLink, useNavigate } from 'react-router-dom';

import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import PasswordOutlinedIcon from '@mui/icons-material/PasswordOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';

import api from '../services/api';
import BrandLogo from '../components/BrandLogo';

const OTP_TOTAL_SECONDS = 180;

function formatTimer(seconds) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${String(minutes).padStart(2, '0')}:${String(
    remainingSeconds
  ).padStart(2, '0')}`;
}

function ForgotPassword({ role }) {
  const navigate = useNavigate();

  const isSeller = role === 'seller';
  const roleLabel = isSeller ? 'Seller' : 'Delivery Partner';
  const loginPath = isSeller ? '/seller/login' : '/dlp/login';
  const apiBasePath = isSeller ? '/auth/seller' : '/auth/dlp';

  const [step, setStep] = useState(1);

  const [telephone, setTelephone] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [timer, setTimer] = useState(0);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (step !== 2 || timer <= 0) return undefined;

    const intervalId = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(intervalId);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [step, timer]);

  const passwordStrength = useMemo(() => {
    let score = 0;

    if (newPassword.length >= 6) score += 25;
    if (newPassword.length >= 8) score += 25;
    if (/[A-Z]/.test(newPassword)) score += 15;
    if (/[0-9]/.test(newPassword)) score += 15;
    if (/[^A-Za-z0-9]/.test(newPassword)) score += 20;

    return Math.min(score, 100);
  }, [newPassword]);

  const passwordStrengthColor = useMemo(() => {
    if (passwordStrength < 40) return 'error';
    if (passwordStrength < 75) return 'warning';
    return 'success';
  }, [passwordStrength]);

  const passwordStrengthLabel = useMemo(() => {
    if (!newPassword) return 'Not started';
    if (passwordStrength < 40) return 'Weak';
    if (passwordStrength < 75) return 'Medium';
    return 'Strong';
  }, [newPassword, passwordStrength]);

  const sendOtp = async () => {
    try {
      setError('');
      setSuccess('');

      if (!telephone.trim()) {
        setError('Please enter your registered mobile number.');
        return;
      }

      setLoading(true);

      await api.post(`${apiBasePath}/forgot-password`, {
        telephone: telephone.trim(),
      });

      setStep(2);
      setOtp('');
      setResetToken('');
      setTimer(OTP_TOTAL_SECONDS);
      setSuccess('If this phone number is registered, an OTP has been sent.');
    } catch (err) {
      setError(
        err.response?.data?.message || 'Failed to send OTP. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    try {
      setError('');
      setSuccess('');

      if (!otp.trim()) {
        setError('Please enter the OTP.');
        return;
      }

      if (!/^\d{6}$/.test(otp.trim())) {
        setError('OTP must be 6 digits.');
        return;
      }

      if (timer <= 0) {
        setError('OTP has expired. Please request a new OTP.');
        return;
      }

      setLoading(true);

      const res = await api.post(`${apiBasePath}/verify-reset-otp`, {
        telephone: telephone.trim(),
        otp: otp.trim(),
      });

      setResetToken(res.data.resetToken);
      setStep(3);
      setSuccess('OTP verified successfully. Please create a new password.');
    } catch (err) {
      setError(
        err.response?.data?.message || 'Failed to verify OTP. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    try {
      setError('');
      setSuccess('');

      if (!newPassword.trim()) {
        setError('Please enter your new password.');
        return;
      }

      if (newPassword.length < 6) {
        setError('Password must be at least 6 characters long.');
        return;
      }

      if (newPassword !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }

      if (!resetToken) {
        setError('Reset token missing. Please verify OTP again.');
        return;
      }

      setLoading(true);

      await api.post(`${apiBasePath}/reset-password`, {
        resetToken,
        newPassword,
        confirmPassword,
      });

      setStep(4);
      setSuccess(
        'Password reset successfully. Please login with your new password.'
      );
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Failed to reset password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    await sendOtp();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background:
          'linear-gradient(135deg, #0F172A 0%, #1E3A8A 48%, #2563EB 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 5,
            boxShadow: '0 30px 80px rgba(15, 23, 42, 0.35)',
          }}
        >
          <Stack alignItems="center" textAlign="center" sx={{ mb: 3 }}>
            <BrandLogo size={58} />

            <Box
              sx={{
                mt: 2,
                width: 70,
                height: 70,
                borderRadius: 4,
                bgcolor: '#DBEAFE',
                color: '#1D4ED8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LockResetOutlinedIcon sx={{ fontSize: 40 }} />
            </Box>

            <Typography variant="h4" fontWeight={900} sx={{ mt: 2 }}>
              {roleLabel} Password Recovery
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
              Reset your password using your registered mobile number.
            </Typography>
          </Stack>

          {error ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>
              {error}
            </Alert>
          ) : null}

          {success ? (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 3 }}>
              {success}
            </Alert>
          ) : null}

          {step === 1 && (
            <Stack spacing={2.4}>
              <Box>
                <Typography variant="h6" fontWeight={900}>
                  Step 1: Enter Mobile Number
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Enter the mobile number registered with your{' '}
                  {roleLabel.toLowerCase()} account.
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Registered Mobile Number"
                placeholder="Example: 771234567"
                value={telephone}
                onChange={(e) => setTelephone(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneOutlinedIcon />
                    </InputAdornment>
                  ),
                }}
                sx={fieldStyle}
              />

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={sendOtp}
                disabled={loading}
                sx={buttonStyle}
              >
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </Button>
            </Stack>
          )}

          {step === 2 && (
            <Stack spacing={2.4}>
              <Box>
                <Typography variant="h6" fontWeight={900}>
                  Step 2: Verify OTP
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Enter the 6-digit OTP sent to your mobile number.
                </Typography>
              </Box>

              <Alert severity={timer > 0 ? 'info' : 'warning'} sx={{ borderRadius: 3 }}>
                {timer > 0
                  ? `OTP expires in ${formatTimer(timer)}`
                  : 'OTP expired. Please resend OTP.'}
              </Alert>

              <TextField
                fullWidth
                label="OTP Code"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                disabled={loading || timer <= 0}
                inputProps={{
                  maxLength: 6,
                  inputMode: 'numeric',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PasswordOutlinedIcon />
                    </InputAdornment>
                  ),
                }}
                sx={fieldStyle}
              />

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={verifyOtp}
                disabled={loading || timer <= 0}
                sx={buttonStyle}
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                onClick={resendOtp}
                disabled={loading || timer > 0}
                sx={{
                  borderRadius: 3,
                  fontWeight: 900,
                  textTransform: 'none',
                }}
              >
                Resend OTP
              </Button>
            </Stack>
          )}

          {step === 3 && (
            <Stack spacing={2.4}>
              <Box>
                <Typography variant="h6" fontWeight={900}>
                  Step 3: Create New Password
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Enter your new password and confirm it.
                </Typography>
              </Box>

              <Box>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={loading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword((prev) => !prev)}
                          edge="end"
                          disabled={loading}
                        >
                          {showNewPassword ? (
                            <VisibilityOffOutlinedIcon />
                          ) : (
                            <VisibilityOutlinedIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldStyle}
                />

                <Box sx={{ mt: 1 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 0.7 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Password strength
                    </Typography>

                    <Typography
                      variant="caption"
                      fontWeight={900}
                      color={`${passwordStrengthColor}.main`}
                    >
                      {passwordStrengthLabel}
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={passwordStrength}
                    color={passwordStrengthColor}
                    sx={{
                      height: 8,
                      borderRadius: 999,
                      bgcolor: 'rgba(148, 163, 184, 0.18)',
                    }}
                  />
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                error={Boolean(confirmPassword) && newPassword !== confirmPassword}
                helperText={
                  confirmPassword && newPassword !== confirmPassword
                    ? 'Passwords do not match'
                    : confirmPassword && newPassword === confirmPassword
                    ? 'Passwords match'
                    : ''
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword((prev) => !prev)
                        }
                        edge="end"
                        disabled={loading}
                      >
                        {showConfirmPassword ? (
                          <VisibilityOffOutlinedIcon />
                        ) : (
                          <VisibilityOutlinedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={fieldStyle}
              />

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={resetPassword}
                disabled={loading}
                sx={buttonStyle}
              >
                {loading ? 'Updating Password...' : 'Update Password'}
              </Button>
            </Stack>
          )}

          {step === 4 && (
            <Stack spacing={2.4} alignItems="center" textAlign="center">
              <Box
                sx={{
                  width: 76,
                  height: 76,
                  borderRadius: '50%',
                  bgcolor: '#DCFCE7',
                  color: '#16A34A',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckCircleOutlineIcon sx={{ fontSize: 44 }} />
              </Box>

              <Typography variant="h6" fontWeight={900}>
                Password Reset Completed
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Your password has been updated successfully. Please login with
                your new password.
              </Typography>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => navigate(loginPath)}
                sx={buttonStyle}
              >
                Go to Login
              </Button>
            </Stack>
          )}

          <Stack alignItems="center" sx={{ mt: 3 }}>
            <Link
              component={RouterLink}
              to={loginPath}
              underline="hover"
              sx={{
                fontWeight: 900,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.6,
              }}
            >
              <ArrowBackOutlinedIcon fontSize="small" />
              Back to {roleLabel} Login
            </Link>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

const fieldStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 3,
    bgcolor: '#fff',
  },
};

const buttonStyle = {
  py: 1.25,
  borderRadius: 3,
  fontWeight: 900,
  textTransform: 'none',
  boxShadow: 'none',
};

export default ForgotPassword;